/* eslint-disable sonarjs/no-use-of-empty-return-value */
// eslint-disable-next-line import/no-extraneous-dependencies
import { PoweroffOutlined } from '@ant-design/icons';
import { Col, Row, Space, message } from 'antd';
import { FieldArray, Formik } from 'formik';
import { Form, Select, TimePicker, AutoComplete } from 'formik-antd';
import { isEmpty } from 'lodash';
import moment from 'moment';
import { PropTypes } from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import QRCode from 'react-qr-code';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import * as Yup from 'yup';
import { getClientList } from 'store/clientSlice';
import { getPincodeList, getListAction } from 'store/pageSlice';
import { addProject } from 'store/projectSlice';
import { pinError, reportingTimeOptions } from 'utils';
import { ActionAddButton, CancelButton } from 'utils/commonStyled';
import { useQuery } from 'hooks';
import { ACL, EditModeBtn } from 'components/common';
import { Box, Button, CustomInput, Flex, FormInput, Image, Text } from 'atoms';
import RipplrLogo from 'assets/ripplr-logo.svg';
import { Debug } from './Debug';
import { commonSearch, getClientGstins, commonUpdate, getSignedUrl } from 'store/commonSlice';
import UploadWithPreview from 'components/common/UploadWithPreview';

const PrintQButton = styled(Button)`
	background: transparent !important;
	background-color: transparent !important;
	color: #1890ff;
	border-radius: 4px;
`;

const TimePickers = styled(TimePicker)`
	width: 100%;
`;
const PrintQrBox = styled(QRCode)`
	padding: 3rem;
	border-radius: 5px;
	border: 2px solid #ababab;
`;

const QrMainBox = styled(Flex)`
	padding: 16px;
	flex-direction: column;
	align-items: center;
	gap: 5rem;
`;

const QrTopBox = styled(Flex)`
	padding: 16px;
	justify-content: space-between;
	gap: 20rem;
`;
const QrMiddleBox = styled(Flex)`
	font-size: 16px;
	gap: 0.5rem;
	flex-direction: column;
	align-items: center;
`;

const BoxSpace = styled(Box)`
	padding: 16px;
	margin-top: 12px;
`;

const FormSchema = Yup.object({
	project_location_code: Yup.string()
		.required()
		.min(3)
		.max(200)
		.label('project location')
		.matches(/^[a-zA-Z0-9 -.]*$/, 'Please enter valid project location code'),
	latitude: Yup.string()
		.required('Latitude is required')
		.test(
			'latitude-format',
			'Invalid Latitude Format should be a number between 1 to 2 digits, can contain 1 to 7 decimal points.',
			function latitude(value) {
				if (isEmpty(value)) return false;
				const regex = /^-?\d{1,2}(\.\d{1,7})?$/;
				if (!regex.test(value)) return false;
				const numValue = parseFloat(value);
				return Number.isFinite(numValue) && numValue >= -90 && numValue <= 90;
			}
		),
	longitude: Yup.string()
		.required('Longitude is required')
		.test(
			'longitude-format',
			'Invalid Longitude Format should be a number between 1 to 3 digits, can contain 1 to 7 decimal points.',
			function longitude(value) {
				if (isEmpty(value)) return false;
				const regex = /^-?\d{1,3}(\.\d{1,7})?$/;
				if (!regex.test(value)) return false;
				const numValue = parseFloat(value);
				return Number.isFinite(numValue) && numValue >= -180 && numValue <= 180;
			}
		),
	adhoc_approval_required: Yup.bool().required().label('Adhoc Approval Required'),
	reporting_time: Yup.string().required().label('Reporting Time'),
	client_id: Yup.string().required().label('Client'),
	pin_code: Yup.string()
		.required()
		.label('Pin Code')
		.min(6, pinError)
		.max(6, pinError)
		.matches(/^[0-9]*$/, 'Please enter valid pin code'),
	zone: Yup.string().label('Zone').required(),
	state: Yup.string().label('State').required(),
	city: Yup.string().label('City').required(),
	minimum_temperature: Yup.number().label('Minimum Temperature'),
	maximum_temperature: Yup.number()
		.label('Maximum Temperature')
		.test(
			'is-greater-than-min',
			'Maximum temperature must be greater than or equal to minimum temperature',
			function (value) {
				const { minimum_temperature } = this.parent;
				if (!value || !minimum_temperature) return true;
				return value >= minimum_temperature;
			}
		),
	wa_group_id: Yup.number(),
	gstin: Yup.string()
		.label('GSTIN')
		.matches(/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[0-9]{1}Z[0-9]{1}$/, 'Invalid GSTIN format'),
	gst_certificate: Yup.string().label('GST Certificate'),
	shifts: Yup.array()
		.of(
			Yup.object().shape({
				time_start: Yup.string().when('reporting_time', (reporting_time, schema) => {
					if (reporting_time === 'shift') return schema.required();
					return schema;
				}),
				time_end: Yup.string().when('reporting_time', (reporting_time, schema) => {
					if (reporting_time === 'shift') return schema.required();
					return schema;
				}),
			})
		)
		.when('reporting_time', (reporting_time, schema) => {
			if (reporting_time === 'shift') return schema.required();
			return schema;
		}),
	business_unit_id: Yup.string(), // Made optional
	cost_center_id: Yup.string().required(),
});

const DATE_FORMAT = 'YYYY-MM-DD';
const TIME_FORMAT = 'hh:mm A';

function getDateTime(timeData) {
	const today = moment().format(DATE_FORMAT);
	const time = moment(timeData, TIME_FORMAT).format(TIME_FORMAT);
	return moment(`${today} ${time}`, `${DATE_FORMAT} ${TIME_FORMAT}`);
}

function getShiftData(shift) {
	return {
		id: Math.random(),
		time_start: shift?.start_time ? getDateTime(shift?.start_time) : undefined,
		time_end: shift?.end_time ? getDateTime(shift?.end_time) : undefined,
	};
}

function generateShiftTimings(shifts) {
	console.log(shifts);
	return shifts?.map((item) => getShiftData(item));
}

function addRemovePayment(arrayHelpers, index) {
	if (index === 0) {
		// arrayHelpers.insert(index + 1, getNewBillingRowData());
		arrayHelpers.push(getShiftData());
	} else {
		arrayHelpers.remove(index);
	}
}
export default function AddProject({ isEditMode = false, formData = {}, clientsData }) {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	const formRef = useRef();
	const [query] = useQuery();
	const [editMode, setEditMode] = useState(false);
	const [isCreate, setIsCreate] = useState(false);
	const [whatsAppGroups, setWhatsAppGroups] = useState([]);
	const [businessUnits, setBusinessUnits] = useState([]);
	const [costCenters, setCostCenters] = useState([]);
	const [gstinOptions, setGstinOptions] = useState([]);
	const [gstinSearchValue, setGstinSearchValue] = useState('');
	const [gstCertificateKey, setGstCertificateKey] = useState('');
	const [gstUploading, setGstUploading] = useState(false);

	const projectType = query.get('project_type');

	const getApproval = (val) => (val === true ? 'true' : 'false');

	useEffect(() => {
		dispatch(
			getListAction({
				search_type: 'business_unit',
				limit: 100,
				offset: 0,
			})
		).then((res) => {
			const buOptions = (res?.rows || []).map((bu) => ({
				value: bu.id,
				label: bu.name,
			}));
			setBusinessUnits(buOptions);
		});

		// Fetch cost centers
		dispatch(
			commonSearch({
				search_type: 'cost_center',
				limit: 100,
				offset: 0,
				fetch_filters: true,
			})
		).then((res) => {
			const costCenterOptions = (res?.rows || []).map((cc) => ({
				value: cc.id,
				label: cc.name || cc.cost_center_name,
			}));
			setCostCenters(costCenterOptions);
		});
	}, [dispatch]);

	const initialValues = useMemo(
		// eslint-disable-next-line sonarjs/cognitive-complexity
		() => ({
			project_location_code: formData?.project_location_code || undefined,
			project_unique_code: formData?.project_unique_code || undefined,
			latitude: formData?.info?.latitude || undefined,
			longitude: formData?.info?.longitude || undefined,
			reporting_time: formData?.reporting_time || undefined,
			client_id: formData?.client_id || undefined,
			pin_code: formData?.pin_code || undefined,
			zone: formData?.zone || undefined,
			state: formData?.state || undefined,
			city: formData?.city || undefined,
			adhoc_approval_required: getApproval(formData?.adhoc_approval_required) || undefined,
			minimum_temperature: formData?.info?.minimum_temperature || 0,
			maximum_temperature: formData?.info?.maximum_temperature || 4,
			wa_group_id: formData?.wa_group_id || undefined,
			shifts:
				formData?.reporting_time === 'shift'
					? generateShiftTimings(formData?.shifts)
					: [getShiftData()],
			business_unit_id: formData?.business_unit_id || undefined,
			cost_center_id: formData?.cost_center_id || undefined,
			supervisors: formData?.supervisors || [],
			gstin: formData?.gstin || undefined,
		}),
		[formData]
	);

	console.log(formData);

	// eslint-disable-next-line sonarjs/cognitive-complexity
	const handleSubmit = async (values, { setErrors, setSubmitting }) => {
		// Validate GST Certificate requirement for new GSTIN
		if (values?.gstin && Array.isArray(gstinOptions)) {
			const existsInList = gstinOptions.some((opt) => opt?.value === values.gstin);
			if (!existsInList && !gstCertificateKey) {
				setErrors({
					gst_certificate: 'GST Certificate is required for new GSTIN',
				});
				setSubmitting(false);
				return;
			}
		}

		const {
			reporting_time,
			shifts,
			adhoc_approval_required,
			minimum_temperature,
			maximum_temperature,
			...rest
		} = values;
		const isShiftReporting = shifts.length > 0 && reporting_time === 'shift';
		const timedata = isShiftReporting
			? shifts.map((item) => ({
					end_time: moment(item?.time_end).format(TIME_FORMAT),
					start_time: moment(item?.time_start).format(TIME_FORMAT),
			  }))
			: undefined;

		const commonPayload = {
			...rest,
			adhoc_approval_required: adhoc_approval_required === 'true',
			reporting_time,
			shifts: timedata,
			info: {
				latitude: values?.latitude,
				longitude: values?.longitude,
				minimum_temperature,
				maximum_temperature,
			},
			gstin_document_info: gstCertificateKey || undefined,
			...(projectType === 'temperature_controlled' && {
				is_temperature_control: true,
			}),
		};

		// Ensure create mode when on /add route (old UI safeguard)
		// Check pathname directly to avoid timing issues with state
		const { pathname } = location;
		const isOnCreatePage = pathname.includes('/add') || pathname.endsWith('/add');
		const actualIsEditMode = isOnCreatePage ? false : isEditMode;

		const payload = actualIsEditMode
			? {
					data: {
						...commonPayload,
						id: formData?.id,
						client_id: formData?.client_id,
						business_unit_id: values?.business_unit_id,
						cost_center_id: values?.cost_center_id,
					},
					update_type: 'project',
			  }
			: {
					data: {
						...commonPayload,
						is_active: true,
						client_name: clientsData?.find(
							(item) => item?.item?.id === values?.client_id
						)?.item?.client_info?.client_name,
						business_unit_id: values?.business_unit_id,
						cost_center_id: values?.cost_center_id,
					},
					create_type: 'project',
			  };

		function handleErrors(errors) {
			const formattedErrors = {};

			Object.keys(errors).forEach((key) => {
				if (typeof errors[key] === 'object' && errors[key] !== null) {
					try {
						if (Array.isArray(errors[key])) {
							formattedErrors[key] = errors[key].join(', ');
						} else {
							formattedErrors[key] = Object.keys(errors[key])
								.map((errorKey) => `${errorKey}: ${errors[key][errorKey]}`)
								.join(', ');
						}
					} catch (e) {
						formattedErrors[key] = JSON.stringify(errors[key]);
					}
				} else {
					formattedErrors[key] = errors[key];
				}
			});

			setErrors(formattedErrors);
		}

		try {
			// If GSTIN entered is new (both create and edit), upsert GSTIN first
			if (values?.gstin && Array.isArray(gstinOptions)) {
				const existsInList = gstinOptions.some((opt) => opt?.value === values.gstin);
				if (!existsInList) {
					await dispatch(
						commonUpdate({
							update_type: 'client_gstin',
							data: {
								client_id: values?.client_id || formData?.client_id,
								gstin: values?.gstin,
								gstin_document_info: gstCertificateKey || undefined,
							},
						})
					);
				}
			}

			await dispatch(
				addProject({ data: payload, setErrors: handleErrors, isEditMode: actualIsEditMode })
			);
			navigate('/projects');
		} catch (e) {
			console.error('error', e);
		} finally {
			setSubmitting(false);
		}
	};

	function handlePicodeChange(e) {
		console.log('handlePicodeChange', e?.target?.value);

		const pin_code = e?.target?.value;

		if (pin_code?.length === 6) {
			dispatch(getPincodeList(pin_code))
				.then((res) => {
					const { city, state, zone } = res?.rows?.[0] || {};
					if (formRef?.current) {
						formRef.current?.setFieldValue('city', city);
						formRef.current?.setFieldValue('state', state);
						formRef.current?.setFieldValue('zone', zone);
					}
				})
				.catch((er) => {
					console.log('pincode error', er);
				});
		} else {
			console.log('not a valid pincode');
			if (formRef?.current) {
				formRef.current?.setFieldValue('city', undefined);
				formRef.current?.setFieldValue('state', undefined);
				formRef.current?.setFieldValue('zone', undefined);
			}
		}
	}

	const Print = () => {
		const printContents = document.getElementById('printablediv').innerHTML;
		const originalContents = document.body.innerHTML;
		document.body.innerHTML = printContents;
		window.print();
		document.body.innerHTML = originalContents;
		window.location.reload();
	};

	const apporvalTypesOptions = [
		{ value: 'true', label: 'Yes' },
		{ value: 'false', label: 'No' },
	];

	function handleClientTypeChange(e, setFieldValue) {
		console.log(e);
		dispatch(
			getClientList({
				search_type: 'client',
				filter_by: {
					id: e,
				},
			})
		).then((res) => {
			console.log(res);
			console.log(res);
			const proFalg = res?.data?.map((item) => ({
				adhoc_approval_required: item?.adhoc_approval_required,
			}));
			console.log(proFalg);
			// if (proFalg) {
			// 	SetProjectAdhocFlag(proFalg[0]?.adhoc_approval_required);
			// }
			if (proFalg[0]?.adhoc_approval_required) {
				setFieldValue('adhoc_approval_required', 'true');
			} else {
				setFieldValue('adhoc_approval_required', 'false');
			}
		});

		// Fetch GSTINs for the selected client
		if (e) {
			dispatch(getClientGstins({ client_id: e }))
				.then((res) => {
					const gstinList = (res?.rows || []).map((gstin) => ({
						value: gstin.gstin,
						label: gstin.gstin,
					}));
					setGstinOptions(gstinList);
					// Reset GSTIN field when client changes
					setFieldValue('gstin', undefined);
					setGstinSearchValue('');
				})
				.catch((error) => {
					console.log('Error fetching GSTINs:', error);
					setGstinOptions([]);
					setFieldValue('gstin', undefined);
					setGstinSearchValue('');
				});
		} else {
			setGstinOptions([]);
			setFieldValue('gstin', undefined);
			setGstinSearchValue('');
		}
	}
	function handleEdit() {
		setEditMode(!editMode);
	}

	const getGstinNotFoundContent = () => {
		if (gstinOptions.length === 0) {
			return 'No GSTINs found for this client. You can enter a new one.';
		}
		return 'No matching GSTINs found. You can enter a new one.';
	};

	useEffect(() => {
		setIsCreate(location.pathname.split('/').pop() === 'add');

		if (isEditMode && formData?.is_temperature_control) {
			projectTypeRef.current = 'temperature_controlled';
		}

		if (projectType === 'temperature_controlled' || formData?.is_temperature_control) {
			dispatch(
				commonSearch({
					search_type: 'whatsapp_group_list',
					limit: 1000,
				})
			).then((res) => setWhatsAppGroups(res?.rows));
		}

		// If in edit mode and client is already chosen, fetch GSTINs immediately
		if (isEditMode && formData?.client_id) {
			dispatch(getClientGstins({ client_id: formData.client_id }))
				.then((res) => {
					const gstinList = (res?.rows || []).map((gst) => ({
						value: gst.gstin,
						label: gst.gstin,
					}));
					setGstinOptions(gstinList);
				})
				.catch(() => {
					setGstinOptions([]);
				});
		}

		// Initialize GSTIN search value when form data is loaded
		if (formData?.gstin) {
			setGstinSearchValue(formData.gstin);
		}
	}, [location, isEditMode, formData]);

	console.log(whatsAppGroups);
	const projectTypeRef = useRef(projectType);

	const showTemperatureFields =
		projectType === 'temperature_controlled' ||
		(isEditMode && formData?.is_temperature_control) ||
		projectTypeRef.current === 'temperature_controlled';

	return (
		<Formik
			initialValues={initialValues}
			validationSchema={FormSchema}
			onSubmit={handleSubmit}
			enableReinitialize
			innerRef={formRef}
			initialTouched={{
				zone: true,
				state: true,
				city: true,
			}}
		>
			{/* eslint-disable-next-line sonarjs/cognitive-complexity */}
			{({ values, errors, setFieldValue }) => (
				<Box>
					<ACL action="update" module="project">
						{isEditMode && <EditModeBtn handleEdit={handleEdit} />}
					</ACL>
					<Form className={editMode === false && !isCreate ? 'disabled-div' : ''}>
						<Box padding="1.5rem 2rem" background="#fff">
							<Row gutter={16} justify="space-between">
								<Col lg={12}>
									<FormInput
										name="project_location_code"
										label="Project Location Code *"
									>
										<CustomInput name="project_location_code" />
									</FormInput>
								</Col>
								<Col lg={12}>
									<FormInput name="client_id" label="Select Client *">
										<Select
											showSearch
											onClear
											name="client_id"
											disabled={isEditMode}
											filterOption={(input, option) =>
												option.label
													.toLowerCase()
													.indexOf(input.toLowerCase()) >= 0
											}
											options={clientsData}
											placeholder="Select"
											onChange={(e) =>
												handleClientTypeChange(e, setFieldValue)
											}
										/>
									</FormInput>
								</Col>
							</Row>

							<Row gutter={16} justify="space-between">
								<Col lg={12}>
									<FormInput name="latitude" label="Latitude *">
										<CustomInput name="latitude" />
									</FormInput>
								</Col>
								<Col lg={12}>
									<FormInput name="longitude" label="Longitude *">
										<CustomInput name="longitude" />
									</FormInput>
								</Col>
							</Row>

							<Row gutter={16} justify="space-between">
								<Col lg={12}>
									<FormInput name="reporting_time" label="Reporting Time *">
										<Select
											disabled={isEditMode}
											name="reporting_time"
											filterOption={(input, option) =>
												option.label
													.toLowerCase()
													.indexOf(input.toLowerCase()) >= 0
											}
											placeholder="Select"
											options={reportingTimeOptions}
										/>
									</FormInput>
								</Col>
								<Col lg={12}>
									<FormInput
										name="pin_code"
										label="Pincode *"
										onChange={handlePicodeChange}
									>
										<CustomInput name="pin_code" maxLength={6} />
									</FormInput>
								</Col>
							</Row>

							<Row gutter={16} justify="space-between">
								<Col lg={12}>
									<FormInput name="zone" label="Zone *">
										<CustomInput name="zone" disabled />
									</FormInput>
								</Col>
								<Col lg={12}>
									<FormInput name="state" label="State *">
										<CustomInput name="state" disabled />
									</FormInput>
								</Col>
							</Row>
							<Row gutter={16} justify="space-between">
								<Col lg={12}>
									<FormInput name="city" label="City *">
										<CustomInput name="city" disabled />
									</FormInput>
								</Col>
								<Col lg={12}>
									<FormInput
										name="adhoc_approval_required"
										label="Approval of Adhoc Trip Needed? *"
									>
										<Select
											name="adhoc_approval_required"
											filterOption={(input, option) =>
												option.label
													.toLowerCase()
													.indexOf(input.toLowerCase()) >= 0
											}
											placeholder="Select"
											options={apporvalTypesOptions}
										/>
									</FormInput>
								</Col>
							</Row>
							{showTemperatureFields && (
								<Row gutter={16} justify="space-between">
									<Col lg={12}>
										<Space>
											<FormInput
												name="minimum_temperature"
												label="Minimum Temperature"
											>
												<CustomInput
													name="minimum_temperature"
													type="number"
												/>
											</FormInput>
											<FormInput
												name="maximum_temperature"
												label="Maximum Temperature"
											>
												<CustomInput
													name="maximum_temperature"
													type="number"
												/>
											</FormInput>
										</Space>
									</Col>
									<Col lg={12}>
										<FormInput name="wa_group_id" label="Whatsapp Group">
											<Select
												name="wa_group_id"
												options={whatsAppGroups}
												allowClear
											/>
										</FormInput>
									</Col>
								</Row>
							)}
							<Row gutter={16} justify="space-between">
								<Col lg={12}>
									<FormInput name="business_unit_id" label="Business Unit">
										{isEditMode ? (
											<CustomInput
												name="business_unit_id"
												value={
													businessUnits.find(
														(bu) => bu.value === values.business_unit_id
													)?.label || ''
												}
												disabled
											/>
										) : (
											<Select
												showSearch
												name="business_unit_id"
												placeholder="Select Business Unit"
												options={businessUnits}
												value={values.business_unit_id}
												onChange={(val) => {
													setFieldValue('business_unit_id', val);
													setFieldValue('supervisors', []); // reset supervisors on BU change
												}}
												allowClear
												filterOption={(input, option) =>
													(option?.label || '')
														.toLowerCase()
														.indexOf(input.toLowerCase()) >= 0
												}
											/>
										)}
									</FormInput>
								</Col>
								<Col lg={12}>
									<FormInput name="cost_center_id" label="Cost Center *">
										<Select
											showSearch
											name="cost_center_id"
											placeholder="Select Cost Center"
											options={costCenters}
											value={values.cost_center_id}
											onChange={(val) => setFieldValue('cost_center_id', val)}
											allowClear
											filterOption={(input, option) =>
												(option?.label || '')
													.toLowerCase()
													.indexOf(input.toLowerCase()) >= 0
											}
										/>
									</FormInput>
								</Col>
							</Row>
							<Row gutter={16} justify="space-between">
								<Col lg={12}>
									{/* <FormInput name="supervisors" label="Supervisors *">
										<Select
											mode="multiple"
											showSearch
											name="supervisors"
											placeholder="Select Supervisor(s)"
											options={supervisorOptions}
											value={values.supervisors}
											onChange={(val) => setFieldValue('supervisors', val)}
											filterOption={(input, option) =>
												(option?.label || '')
													.toLowerCase()
													.indexOf(input.toLowerCase()) >= 0
											}
										/>
										{errors.supervisors && (
											<Text color="red">{errors.supervisors}</Text>
										)}
									</FormInput> */}
								</Col>
							</Row>
							{/* add it here */}
							<Row gutter={16} justify="space-between">
								<Col lg={12}>
									<FormInput name="gstin" label="GSTIN">
										<AutoComplete
											name="gstin"
											disabled={!values.client_id}
											options={gstinOptions}
											placeholder="Select or enter GSTIN"
											value={gstinSearchValue}
											onSearch={(value) => setGstinSearchValue(value)}
											onSelect={(value) => {
												setFieldValue('gstin', value);
												setGstinSearchValue(value);
												// Clear GST certificate when selecting existing GSTIN
												if (
													gstinOptions.some((opt) => opt.value === value)
												) {
													setGstCertificateKey('');
												}
											}}
											onChange={(value) => {
												setGstinSearchValue(value);
												setFieldValue('gstin', value);
												// Clear GST certificate when entering new GSTIN
												if (
													!gstinOptions.some((opt) => opt.value === value)
												) {
													setGstCertificateKey('');
												}
											}}
											notFoundContent={getGstinNotFoundContent()}
											filterOption={(input, option) =>
												option.label
													.toLowerCase()
													.indexOf(input.toLowerCase()) >= 0
											}
											allowClear
										/>
									</FormInput>
								</Col>
								<Col lg={12}>
									{values?.gstin &&
										Array.isArray(gstinOptions) &&
										!gstinOptions.some(
											(opt) => opt?.value === values.gstin
										) && (
											<>
												<FormInput
													name="gst_certificate"
													label="GST Certificate (PDF/Image)"
													required
												>
													<UploadWithPreview
														beforeUpload={async (file) => {
															const max = 5 * 1024 * 1000;
															const allowed = [
																'.pdf',
																'.jpg',
																'.jpeg',
																'.png',
															];
															if (file.size > max) {
																message.error(
																	'File size must be less than 5 MB'
																);
																return false;
															}
															const ext = `.${file.name
																.split('.')
																.pop()
																.toLowerCase()}`;
															if (!allowed.includes(ext)) {
																message.error(
																	'Only PDF/JPG/PNG files are allowed'
																);
																return false;
															}
															setGstUploading(true);
															try {
																const res = await dispatch(
																	getSignedUrl({
																		url_type: 'upload',
																		data: {
																			entity: 'gst_certificate',
																			keys: [file.name],
																		},
																	})
																);
																const fileData = res?.data?.[0];
																if (
																	!fileData?.url ||
																	!fileData?.filename
																)
																	throw new Error(
																		'Signed URL error'
																	);
																await fetch(fileData.url, {
																	method: 'PUT',
																	body: file,
																	headers: {
																		'Content-Type': file.type,
																	},
																});
																setGstCertificateKey(
																	fileData.filename
																);
																message.success(
																	'GST certificate uploaded'
																);
															} catch (err) {
																message.error('Upload failed');
															} finally {
																setGstUploading(false);
															}
															return false;
														}}
														uploading={gstUploading}
														disabled={false}
														uploadedFile={gstCertificateKey}
														onFileRemove={() =>
															setGstCertificateKey('')
														}
														buttonText="Choose GST Certificate"
														accept={['.pdf', '.jpg', '.jpeg', '.png']}
														entity="gst_certificate"
													/>
												</FormInput>
												<Text color="#666" fontSize="12px" mt="4px">
													Certificate required for new GSTIN
												</Text>
											</>
										)}
								</Col>
							</Row>
							<Row gutter={16} justify="space-between">
								<Col lg={12}>
									{errors?.zone && (
										<Text mt="2.5rem" color="red">
											Please enter valid pincode. Zone, State, City are
											Required *
										</Text>
									)}
								</Col>
								<Col lg={12}></Col>
							</Row>
							{values?.reporting_time === 'shift' && (
								<FieldArray
									name="shifts"
									render={(arrayHelpers) => (
										<div>
											{values.shifts?.map((rowData, index) => (
												<BoxSpace key={rowData?.id}>
													<Row gutter={16} justify="end">
														{index > 0 && (
															<CancelButton
																onClick={() =>
																	addRemovePayment(
																		arrayHelpers,
																		index
																	)
																}
																type="button"
																icon={<PoweroffOutlined />}
															>
																{index === 0
																	? 'Add more Shifts'
																	: 'Remove'}
															</CancelButton>
														)}
													</Row>
													<Row gutter={16} justify="space-between">
														<Col lg={12}>
															<FormInput
																name={`shifts[${index}].time_start`}
																label="Start Time *"
															>
																<TimePickers
																	use12Hours
																	format={TIME_FORMAT}
																	name={`shifts[${index}].time_start`}
																/>
															</FormInput>
														</Col>
														<Col lg={12}>
															<FormInput
																name={`shifts[${index}].time_end`}
																label="End Time *"
															>
																<TimePickers
																	name={`shifts[${index}].time_end`}
																	use12Hours
																	format={TIME_FORMAT}
																/>
															</FormInput>
														</Col>
													</Row>

													{/* adhoc_approval_required */}

													<Row gutter={16} justify="space-between">
														<Col span={4}>
															{index === 0 && (
																<ActionAddButton
																	onClick={() =>
																		addRemovePayment(
																			arrayHelpers,
																			index
																		)
																	}
																	icon={<PoweroffOutlined />}
																	type="button"
																>
																	{index === 0
																		? '+ Add more Shifts'
																		: 'Remove'}
																</ActionAddButton>
															)}
														</Col>
													</Row>
												</BoxSpace>
											))}
										</div>
									)}
								/>
							)}
						</Box>
						<Box>
							{isEditMode && (
								<>
									<PrintQButton mt={3} onClick={Print}>
										Print or Download QR Code
									</PrintQButton>

									<Box id="printablediv">
										<Box id="project_qr_code">
											<QrMainBox>
												<QrTopBox>
													<Box>
														<Image
															src={RipplrLogo}
															alt="Ripplr Cartoon"
														/>
													</Box>
												</QrTopBox>
												<QrMiddleBox>
													{initialValues?.project_unique_code ? (
														<PrintQrBox
															id="qr-gen"
															value={String(
																initialValues.project_unique_code
															)}
															size={300}
														/>
													) : (
														<Text>
															QR Code will be available after project
															is created
														</Text>
													)}
													<Text>
														Use the{' '}
														<span style={{ fontWeight: 600 }}>
															Ripplr App
														</span>{' '}
														to scan this
													</Text>
												</QrMiddleBox>
												<Box>
													<Text>
														1. Open{' '}
														<span style={{ fontWeight: 600 }}>
															{' '}
															&ldquo;Play Store&rdquo;
														</span>{' '}
														App
													</Text>
													<Text>
														2. Search for{' '}
														<span style={{ fontWeight: 600 }}>
															&ldquo;Ripplr&rdquo;
														</span>
													</Text>
													<Text>
														3. Install the app &{' '}
														<span style={{ fontWeight: 600 }}>
															Scan
														</span>
													</Text>
												</Box>
											</QrMainBox>
										</Box>
									</Box>
								</>
							)}
						</Box>
						<Box backgroundColor="#f5f5f5" textAlign="right" padding="1.5rem">
							<CancelButton onClick={() => navigate('/projects')} type="button">
								Cancel
							</CancelButton>
							<Button borderRadius="4px" backgroundColor="#18405d" type="submit">
								{isEditMode ? 'Update' : 'Submit'}
							</Button>
						</Box>
						<Debug />
					</Form>
				</Box>
			)}
		</Formik>
	);
}

AddProject.propTypes = {
	isEditMode: PropTypes.bool,
	formData: PropTypes.object,
	clientsData: PropTypes.any,
};
